import React, { useState } from 'react';
import cc from 'classcat';
import {
  useController,
  useFormContext,
  type FieldValues,
  type Path,
  type RegisterOptions
} from 'react-hook-form';
import { OTPInput, type SlotProps } from 'input-otp';
import { nanoid } from 'nanoid';
import { FieldError } from '../FieldError';
import { FieldLabel } from '../FieldLabel';
import { VerificationCodeInputSlot } from './components';
import './verificationCodeInput.css';

interface VerificationCodeInputProps<T extends FieldValues> {
  name: Path<T>;
  label: string;
  maxLength?: number;
  rules?: RegisterOptions<T>;
}

const VerificationCodeInputSlots = ({ slots }: { slots: SlotProps[] }) => {
  const [keys] = useState(() => slots.map(() => nanoid()));
  return (
    <div className="verificationCodeInput__fieldSlots">
      {slots.map((slot, idx) => (
        <VerificationCodeInputSlot key={keys[idx]} {...slot} placeholderChar="-" />
      ))}
    </div>
  );
};

export const VerificationCodeInput = <T extends FieldValues>({
  name,
  label,
  maxLength = 6,
  rules
}: VerificationCodeInputProps<T>) => {
  const { control } = useFormContext<T>();
  const {
    field,
    fieldState: { error }
  } = useController({ name, control, rules });

  const handleChange = (value: string) => {
    const numericValue = value.replace(/[^0-9]/g, '');
    field.onChange(numericValue);
  };

  return (
    <div className="verificationCodeInput">
      <FieldLabel name={name}>{label}</FieldLabel>
      <OTPInput
        containerClassName={cc([
          'verificationCodeInput__field',
          { 'verificationCodeInput__field--error': error }
        ])}
        {...field}
        id={field.name}
        onChange={handleChange}
        inputMode="numeric"
        maxLength={maxLength}
        render={({ slots }) => <VerificationCodeInputSlots slots={slots} />}
      />
      <FieldError name={name} />
    </div>
  );
};
